import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import type { KadryOverviewRunning, KadryOverviewSearch } from '@entities/kadry';
import { LivePulse, Panel, PanelHead, ago, inTime, toneColor } from './ui';

/* Поиск на площадках: сводка за сутки и идущие сейчас прогоны. */

const Strip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(5)}`};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Fact = styled.span<{ $danger?: boolean }>`
  & > strong {
    margin-right: 4px;
    font-variant-numeric: tabular-nums;
    color: ${({ theme, $danger }) => toneColor(theme, $danger ? 'danger' : 'default')};
  }
`;

const RunList = styled.ul`
  list-style: none;
  margin: ${({ theme }) => `${theme.spacing(4)} 0 0`};
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const RunRow = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const RunTitle = styled.span`
  min-width: 0;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RunMeta = styled.span`
  margin-left: auto;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const TRIGGER_LABELS = { manual: 'вручную', auto: 'автозапуск', schedule: 'по расписанию' } as const;

function since(iso: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return min < 1 ? 'меньше минуты' : `${min} мин`;
}

interface Props {
  s: KadryOverviewSearch;
  running: KadryOverviewRunning[];
}

export function SearchPanel({ s, running }: Props) {
  const navigate = useNavigate();
  return (
    <Panel>
      <PanelHead>
        <h3>Поиск на площадках</h3>
        <span>{running.length > 0 ? `идёт: ${running.length}` : 'сейчас не идёт'}</span>
      </PanelHead>
      <Strip>
        <Fact>
          <strong>{s.runs_24h}</strong>прогонов за сутки
        </Fact>
        {s.failed_24h > 0 && (
          <Fact $danger>
            <strong>{s.failed_24h}</strong>с ошибкой
          </Fact>
        )}
        <Fact>
          последний завершён: {s.last_finished_at ? ago(s.last_finished_at) : 'ещё не было'}
        </Fact>
        {s.next_at && <Fact>следующий: {inTime(s.next_at)}</Fact>}
      </Strip>
      {running.length > 0 && (
        <RunList>
          {running.map((run) => (
            <RunRow
              key={run.request_id}
              tabIndex={0}
              onClick={() => navigate(`/agents/kadry/requests/${run.request_id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') navigate(`/agents/kadry/requests/${run.request_id}`);
              }}
            >
              <LivePulse />
              <RunTitle>{run.title}</RunTitle>
              <RunMeta>
                {TRIGGER_LABELS[run.trigger]} · идёт {since(run.started_at)}
              </RunMeta>
            </RunRow>
          ))}
        </RunList>
      )}
    </Panel>
  );
}
